import type { CredentialDto, VaultRevealPurpose } from '@fos/shared/contracts/vault';
import { VAULT_MESSAGES, VAULT_REVEAL_VISIBLE_SEC } from '@fos/shared/contracts/vault';
import { Copy, Eye, History, Mail, ShieldCheck } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { errorMessage } from '../../../../lib/api';
import { FormMessage } from '../../../../ui/Blocks';
import { Button } from '../../../../ui/Button';
import { Dialog } from '../../../../ui/Dialog';
import { StepUpCodeFields, useStepUpCode } from '../../../auth/components/StepUpCode';

/**
 * „Potwierdź, że to Ty” (Figma 08 „Pokaż dostęp”, 3045:4470) — Fala 7: kodem z maila. „Wyślij kod” → 6 cyfr (pola
 * jak w Figmie, wklejanie, „Wyślij ponownie” z odliczaniem) → „Pokaż hasło” / „Kopiuj hasło”: kod potwierdza sesję
 * na 5 min (`requireStepUp(ctx, { kind: 'second_factor' })`), potem `onConfirmed` pobiera JEDEN sekret
 * (`vault.reveal`). Kod jest czyszczony po każdej próbie i przy zamknięciu; fokus wraca na przycisk, który otworzył
 * okno (Dialog).
 */
export function RevealDialog({
  open,
  onOpenChange,
  credential,
  assetName,
  purpose,
  onConfirmed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credential: Pick<CredentialDto, 'id' | 'label'> | null;
  assetName: string;
  purpose: VaultRevealPurpose;
  /** po potwierdzeniu kodem: podgląd albo kopia; błąd zostaje w oknie */
  onConfirmed: () => Promise<void>;
}) {
  const code = useStepUpCode();
  const { reset } = code;
  const [error, setError] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  // kod przyjęty (sesja ma świeży drugi składnik na 5 min): ponowna próba odczytu bez nowego kodu
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    reset();
    setError(null);
    setRevealing(false);
    setConfirmed(false);
  }, [open, reset]);

  const confirm = async (value?: string) => {
    setError(null);
    if (!confirmed) {
      if (!(await code.verify(value))) return;
      setConfirmed(true);
    }
    setRevealing(true);
    try {
      await onConfirmed();
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRevealing(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!code.sent) void code.send();
    else void confirm();
  };

  const label = credential?.label ?? '';
  const copy = purpose === 'copy';
  const pending = code.pending || revealing;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      width="s"
      title="Potwierdź, że to Ty"
      description={
        copy
          ? `Skopiujemy hasło „${label}” (${assetName}) do schowka.`
          : `Pokażemy hasło „${label}” (${assetName}) na ${VAULT_REVEAL_VISIBLE_SEC} sekund.`
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          {code.sent ? (
            <Button variant="primary" type="submit" form="vault-reveal-form" icon={copy ? Copy : Eye} loading={pending}>
              {copy ? 'Kopiuj hasło' : 'Pokaż hasło'}
            </Button>
          ) : (
            <Button variant="primary" type="submit" form="vault-reveal-form" icon={Mail} loading={pending}>
              Wyślij kod
            </Button>
          )}
        </>
      }
    >
      <form id="vault-reveal-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {code.sent ? (
          <StepUpCodeFields state={code} onComplete={(value) => void confirm(value)} />
        ) : (
          <p className="text-body-s text-fg-secondary">
            Wyślemy 6-cyfrowy kod na adres e-mail Twojego konta. Wpisz go tutaj, a potem pokażemy hasło.
          </p>
        )}
        <div className="flex items-start gap-2.5 rounded-lg bg-surface px-3 py-2.5">
          <History size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
          <p className="text-caption text-fg-secondary">{VAULT_MESSAGES.revealNote}</p>
        </div>
        <p className="flex items-start gap-1.5 text-caption text-fg-muted">
          <ShieldCheck size={13} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
          {VAULT_MESSAGES.codeNote}
        </p>
        {!code.sent && code.error && <FormMessage>{code.error}</FormMessage>}
        {error && <FormMessage>{error}</FormMessage>}
      </form>
    </Dialog>
  );
}
